import {ChangeDetectionStrategy, Component, Input, OnInit} from '@angular/core';
import {DistributionAccount} from '../../../../distribution-account/distribution-account.model';
import {PubmaticFacadeService} from './pubmatic-facade.service';
import {BehaviorSubject, mergeMap, Observable, of} from 'rxjs';
import {PubmaticBuyer, PubmaticViewModel} from './pubmatic-state';
import {exhaustMap, filter, first, map, shareReplay, withLatestFrom} from 'rxjs/operators';


export interface PubmaticEditConfig {
  price: string;
  buyers: PubmaticBuyer[];
}


@Component({
  selector: 'app-account-pubmatic-edit-wrapper',
  template: `
    <div *ngIf="pubmaticModel$ | async; else spinner" class="min-height-250">
      <app-account-pubmatic [account]="account"
                            [pubmaticModel]="pubmaticModel$ | async"
                            [target]="target"
                            (configChange)="onConfigChanged($event)"></app-account-pubmatic>
      <div class="save-button-wrapper">
        <mat-card>
          <mat-card-content>
            <button mat-raised-button class="save-button"
                    color="primary"
                    [disabled]="(shouldDisableSaveButton$ | async) === true"
                    (click)="onClickSave()">
              Save changes
            </button>
          </mat-card-content>
        </mat-card>
      </div>
    </div>

    <ng-template #spinner>
      <mat-spinner class="min-height-250 center"></mat-spinner>
    </ng-template>
  `,
  styleUrls: ['./account-pubmatic-edit-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountPubmaticEditWrapperComponent implements OnInit {
  @Input() account!: DistributionAccount;
  @Input() target!: number;
  pubmaticModel$!: Observable<PubmaticViewModel>;
  shouldDisableSaveButton$!: Observable<boolean>;
  config$: BehaviorSubject<PubmaticEditConfig> = new BehaviorSubject<PubmaticEditConfig>({price:'',buyers:[]});

  constructor(public facade: PubmaticFacadeService) {
  }

  onConfigChanged(config: PubmaticEditConfig) {
    this.config$.next(config);
  }

  onClickSave() {
    this.shouldDisableSaveButton$.pipe(
      filter(isDisabled => !isDisabled),
      first(),
      mergeMap(() => this.config$),
      withLatestFrom(this.pubmaticModel$),
      exhaustMap(([config, originalModel]) => {
        this.facade.saveChanges(this.account, config, originalModel);
        return of(true);
      }),
      first())
      .subscribe();
  }

  ngOnInit(): void {
    this.facade.fetchModel(this.account.foreignIdForCurrentSegment);
    this.pubmaticModel$ = this.facade
      .selectModel(this.account.foreignIdForCurrentSegment)
      .pipe(shareReplay(1));
    this.shouldDisableSaveButton$ = this.config$
      .pipe(
        withLatestFrom(this.pubmaticModel$),
        map(([config, model]) => this.shouldDisableUpdatingButton(config, model)),
        shareReplay(1)
      );
  }

  private shouldDisableUpdatingButton(config: PubmaticEditConfig, model: PubmaticViewModel) {
    // we haven't received any changes yet
    if (!config.price || !config.buyers) {
      return true;
    }
    const changedBuyerIds = config.buyers?.map(buyer => buyer.id) || [];
    return (model.price === config.price
      && (changedBuyerIds.length === model.buyerIds.length
        && model.buyerIds.every(originalBuyerId => changedBuyerIds.includes(originalBuyerId)))
    );
  }
}
